import type { ChatUser, StreamEvent } from './events';

export const EULER_WS_BASE = 'wss://ws.eulerstream.com';

export function buildEulerWsUrl(username: string, jwt: string): string {
  const u = new URL(EULER_WS_BASE);
  u.searchParams.set('uniqueId', username);
  u.searchParams.set('jwtKey', jwt);
  return u.toString();
}

/** Raw envelope Euler sends: `{ messages: [{ type, data }], timestamp }`. */
export interface EulerEnvelope {
  messages?: Array<{ type?: string; data?: unknown }>;
  timestamp?: number;
}

type Rec = Record<string, unknown>;
const rec = (v: unknown): Rec => (v && typeof v === 'object' ? (v as Rec) : {});
const str = (v: unknown, d = ''): string => (typeof v === 'string' ? v : typeof v === 'number' ? String(v) : d);
const num = (v: unknown, d = 0): number => {
  const n = typeof v === 'number' ? v : typeof v === 'string' ? Number(v) : NaN;
  return Number.isFinite(n) ? n : d;
};

function user(raw: unknown): ChatUser {
  const u = rec(raw);
  const pic = rec(u.profilePicture);
  const urls = Array.isArray(pic.url) ? (pic.url as unknown[]) : [];
  const avatar = str(urls[0]) || str(u.avatarUrl) || undefined;
  const uniqueId = str(u.uniqueId) || str(u.userId) || 'unknown';
  return { uniqueId, nickname: str(u.nickname) || uniqueId, avatar };
}

let seq = 0;
function msgId(data: Rec, ts: number): string {
  const id = str(rec(data.common).msgId);
  return id || `${ts}-${++seq}`;
}

/** Converts one Euler envelope into normalized events. Unknown message types are ignored. */
export function normalizeEulerEnvelope(env: EulerEnvelope): StreamEvent[] {
  const out: StreamEvent[] = [];
  const ts = num(env.timestamp, Date.now());
  for (const m of env.messages ?? []) {
    const ev = normalizeEulerMessage(str(m?.type), m?.data, ts);
    if (ev) out.push(ev);
  }
  return out;
}

export function normalizeEulerMessage(type: string, raw: unknown, ts = Date.now()): StreamEvent | null {
  const d = rec(raw);
  switch (type) {
    case 'WebcastChatMessage': {
      const text = str(d.comment).trim();
      if (!text) return null;
      return { t: 'chat', id: msgId(d, ts), user: user(d.user), text, ts };
    }
    case 'WebcastGiftMessage': {
      const g = rec(d.giftDetails);
      const giftType = num(g.giftType);
      const streakEnd = giftType !== 1 || num(d.repeatEnd) === 1;
      const img = rec(g.giftImage ?? g.icon);
      const urls = Array.isArray(img.url) ? (img.url as unknown[]) : [];
      return {
        t: 'gift',
        id: msgId(d, ts),
        user: user(d.user),
        giftName: str(g.giftName) || 'gift',
        giftId: num(d.giftId),
        count: Math.max(1, num(d.repeatCount, 1)),
        diamonds: num(g.diamondCount),
        streakEnd,
        image: str(urls[0]) || undefined,
        ts,
      };
    }
    case 'WebcastLikeMessage':
      return { t: 'like', id: msgId(d, ts), user: user(d.user), count: num(d.likeCount, 1), total: num(d.totalLikeCount), ts };
    case 'WebcastSocialMessage': {
      const displayType = str(rec(rec(d.common).displayText).displayType).toLowerCase();
      const action = num(d.action);
      const isFollow = action === 1 || displayType.includes('follow');
      return { t: isFollow ? 'follow' : 'share', id: msgId(d, ts), user: user(d.user), ts };
    }
    case 'WebcastMemberMessage': {
      if (num(d.action, 1) !== 1) return null;
      return { t: 'join', id: msgId(d, ts), user: user(d.user), ts };
    }
    case 'WebcastRoomUserSeqMessage':
      return { t: 'stats', viewers: num(d.viewerCount), ts };
    case 'roomInfo': {
      const info = rec(d.roomInfo);
      const host = user(d.user);
      if (!str(rec(d.user).uniqueId) && str(d.uniqueId)) host.uniqueId = str(d.uniqueId);
      return { t: 'room', host, title: str(info.title) || undefined, isLive: info.isLive !== false, ts };
    }
    case 'WebcastControlMessage': {
      const map: Record<number, 'paused' | 'unpaused' | 'ended' | 'suspended'> = { 1: 'paused', 2: 'unpaused', 3: 'ended', 4: 'suspended' };
      const action = map[num(d.action)];
      return action ? { t: 'control', action, ts } : null;
    }
    default:
      return null;
  }
}
